import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BrandNamingCalculator = () => {
  const [nameData, setNameData] = useState({
    brandName: '',
    category: '',
    targetAudience: '',
    brandValue: '',
    uniqueSellingPoint: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateBrandName = () => {
    const { brandName, category, targetAudience, brandValue, uniqueSellingPoint } = nameData;
    
    if (!brandName.trim() || !category.trim() || !targetAudience.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate brand name components
    const brandNameComponents = {
      fullName: `${brandName} (${category}) - ${targetAudience} - ${brandValue}${uniqueSellingPoint ? ` - ${uniqueSellingPoint}` : ''}`,
      shortName: `${brandName} - ${category}`,
      brandTagline: `${brandValue}${uniqueSellingPoint ? ` - ${uniqueSellingPoint}` : ''}`,
      brandIdentity: `${brandName} (${category}) - ${brandValue}`,
      brandStatement: `${brandName} - ${targetAudience} - ${brandValue}`
    };

    // Generate comprehensive naming rules
    const namingRules = {
      brand: 'Brand name should be memorable, unique, and easy to pronounce. It should reflect the brand personality and values.',
      category: 'Category should clearly define the product/service type and help position the brand in the market. Use specific industry terms.',
      audience: 'Target audience should be specific and well-defined. Use demographic and psychographic characteristics to create a clear profile.',
      value: 'Brand value should reflect the core proposition and emotional connection with the audience. It should be compelling and authentic.',
      usp: 'Unique Selling Point should highlight competitive advantage and differentiate the brand from competitors. It should be specific and verifiable.',
      identity: 'Brand identity should be consistent across all touchpoints and reflect the brand personality. Use consistent messaging and visual elements.',
      statement: 'Brand statement should clearly communicate the brand purpose and value proposition. It should resonate with the target audience.'
    };

    // Generate practical example usage
    const exampleUsage = {
      marketing: `Use ${brandNameComponents.fullName} in all marketing materials. Highlight ${brandValue} in your key messaging.`,
      socialMedia: `Post about ${brandName} on social media targeting ${targetAudience}. Use ${brandValue} as your core message.`,
      branding: `Develop ${brandName} brand guidelines based on ${brandValue}. Create visual identity that reflects ${brandNameComponents.brandIdentity}.`,
      advertising: `Use ${brandNameComponents.brandStatement} in your advertising campaigns. Focus on ${targetAudience} in your messaging.`,
      customerExperience: `Ensure all customer touchpoints reflect ${brandValue}. Use ${brandNameComponents.brandIdentity} consistently across all channels.`,
      internal: `Train employees on ${brandNameComponents.brandStatement}. Use ${brandValue} as your core business principle.`,
      website: `Create a website that embodies ${brandNameComponents.brandIdentity}. Use ${targetAudience} personas to guide content creation.`,
      product: `Develop products that align with ${brandValue}. Position them according to ${brandNameComponents.brandStatement}.`
    };

    // Generate detailed calculation steps
    const calculationSteps = [
      'Step 1: Analyze Brand Components',
      `Brand Name: ${brandName} (Should be memorable and unique)`,
      `Category: ${category} (Defines market position)`,
      `Target Audience: ${targetAudience} (Defines customer base)`,
      `Brand Value: ${brandValue} (Core proposition)`,
      `USP: ${uniqueSellingPoint || 'None'} (Competitive advantage)`,
      '\nStep 2: Create Brand Identity',
      `Full Name: ${brandNameComponents.fullName} (Complete brand representation)`,
      `Short Name: ${brandNameComponents.shortName} (Concise identifier)`,
      `Brand Tagline: ${brandNameComponents.brandTagline} (Value proposition)`,
      `Brand Identity: ${brandNameComponents.brandIdentity} (Core identity)`,
      `Brand Statement: ${brandNameComponents.brandStatement} (Purpose statement)`,
      '\nStep 3: Apply Naming Rules',
      `Brand: ${namingRules.brand}`,
      `Category: ${namingRules.category}`,
      `Audience: ${namingRules.audience}`,
      `Value: ${namingRules.value}`,
      `USP: ${namingRules.usp}`,
      `Identity: ${namingRules.identity}`,
      `Statement: ${namingRules.statement}`
    ];

    // Generate enhanced chart data
    const chartData = {
      bar: {
        labels: ['Brand', 'Category', 'Audience', 'Value', 'USP', 'Identity', 'Statement'],
        datasets: [{
          label: 'Brand Components',
          data: [1, 2, 3, 4, uniqueSellingPoint ? 5 : 0, 6, 7],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            uniqueSellingPoint ? 'rgba(255, 159, 64, 0.5)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 0.5)',
            'rgba(255, 205, 86, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            uniqueSellingPoint ? 'rgba(255, 159, 64, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 1)',
            'rgba(255, 205, 86, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Brand', 'Category', 'Audience', 'Value', 'USP', 'Identity', 'Statement'],
        datasets: [{
          data: [1, 1, 1, 1, uniqueSellingPoint ? 1 : 0, 1, 1],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            uniqueSellingPoint ? 'rgba(201, 203, 207, 0.7)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 0.7)',
            'rgba(255, 205, 86, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(153, 102, 255, 1)',
            uniqueSellingPoint ? 'rgba(201, 203, 207, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 1)',
            'rgba(255, 205, 86, 1)'
          ],
          borderWidth: 1
        }]
      }
    };

    const newResults = {
      brandNameComponents,
      namingRules,
      exampleUsage,
      calculationSteps
    };

    setResults(newResults);
    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Brand Naming Calculator | Generate Brand Names</title>
        <meta name="description" content="Generate brand names with our Brand Naming Calculator. Get accurate naming conventions for your business." />
        <meta name="keywords" content="brand naming, business naming, brand identity, brand value, unique selling point" />
        <meta property="og:title" content="Brand Naming Calculator | Generate Brand Names" />
        <meta property="og:description" content="Generate brand names with our Brand Naming Calculator. Get accurate naming conventions for your business." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/brand-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🏷️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Brand Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate Brand Names for Your Business</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you generate brand names using standardized naming conventions. It provides naming rules and examples for different brand components.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="brandName">
            Brand Name:
          </label>
          <input
            type="text"
            id="brandName"
            name="brandName"
            value={nameData.brandName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the brand name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
            Category:
          </label>
          <input
            type="text"
            id="category"
            name="category"
            value={nameData.category}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the category (e.g., Technology, Fashion, Food)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="targetAudience">
            Target Audience:
          </label>
          <input
            type="text"
            id="targetAudience"
            name="targetAudience"
            value={nameData.targetAudience}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the target audience (e.g., Millennials, Professionals, Families)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="brandValue">
            Brand Value:
          </label>
          <input
            type="text"
            id="brandValue"
            name="brandValue"
            value={nameData.brandValue}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the brand value proposition"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="uniqueSellingPoint">
            Unique Selling Point (optional):
          </label>
          <input
            type="text"
            id="uniqueSellingPoint"
            name="uniqueSellingPoint"
            value={nameData.uniqueSellingPoint}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the unique selling point"
          />
        </div>

        <button
          onClick={generateBrandName}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Brand Name
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="calculator-section mt-12"
          >
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-6 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Brand Name</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Full Name</span>
                      <span className="font-medium">{results.brandNameComponents.fullName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Short Name</span>
                      <span className="font-medium">{results.brandNameComponents.shortName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Brand Tagline</span>
                      <span className="font-medium">{results.brandNameComponents.brandTagline}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Brand Identity</span>
                      <span className="font-medium">{results.brandNameComponents.brandIdentity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Brand Statement</span>
                      <span className="font-medium">{results.brandNameComponents.brandStatement}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Naming Rules</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Brand</span>
                      <span className="font-medium">{results.namingRules.brand}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Category</span>
                      <span className="font-medium">{results.namingRules.category}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Audience</span>
                      <span className="font-medium">{results.namingRules.audience}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Value</span>
                      <span className="font-medium">{results.namingRules.value}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">USP</span>
                      <span className="font-medium">{results.namingRules.usp}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Identity</span>
                      <span className="font-medium">{results.namingRules.identity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Statement</span>
                      <span className="font-medium">{results.namingRules.statement}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Marketing</span>
                      <span className="font-medium">{results.exampleUsage.marketing}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Social Media</span>
                      <span className="font-medium">{results.exampleUsage.socialMedia}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Branding</span>
                      <span className="font-medium">{results.exampleUsage.branding}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Advertising</span>
                      <span className="font-medium">{results.exampleUsage.advertising}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Customer Experience</span>
                      <span className="font-medium">{results.exampleUsage.customerExperience}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Internal</span>
                      <span className="font-medium">{results.exampleUsage.internal}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Website</span>
                      <span className="font-medium">{results.exampleUsage.website}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Product</span>
                      <span className="font-medium">{results.exampleUsage.product}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Component Chart</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData.bar}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Component Level'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Brand Component'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Brand Component Levels'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold mb-4">Naming Chart</h4>
                  <div className="h-96">
                    <Pie
                      data={chartData.pie}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Brand Components'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Brand Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Brand Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Brand Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the brand name</li>
                <li>Input the category (required)</li>
                <li>Input the target audience (required)</li>
                <li>Enter the brand value proposition</li>
                <li>Optionally add unique selling point</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Brand Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Brand Name" button</li>
                <li>Review the generated brand name</li>
                <li>Check the naming rules</li>
                <li>Verify the example usage</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the full name, short name, and brand tagline</li>
                <li>Review the naming rules for each component</li>
                <li>Verify the example usage for different scenarios</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is brand naming?</h3>
              <p className="text-gray-700">
                Brand naming is the process of creating a unique and memorable name that represents a company, product, or service. It includes brand name, category, target audience, and value proposition.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What components are required?</h3>
              <p className="text-gray-700">
                The minimum required components are:
                <br/><br/>
                - Brand Name: Unique identifier for the business
                <br/>
                - Category: Defines the product/service type
                <br/>
                - Target Audience: Specific group of customers
              </p>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Why is brand naming important?</h3>
              <p className="text-gray-700">
                Brand naming helps in:
                <br/><br/>
                - Building brand identity
                <br/>
                - Creating brand recognition
                <br/>
                - Communicating brand value
                <br/>
                - Differentiating from competitors
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is brand value proposition?</h3>
              <p className="text-gray-700">
                Brand value proposition is a statement that clearly communicates the unique benefits and value that a brand offers to its customers. It answers the question: "Why should customers choose us?"
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is unique selling point (USP)?</h3>
              <p className="text-gray-700">
                Unique Selling Point is a specific feature or benefit that makes a brand stand out from its competitors. It highlights what makes the brand special and why customers should choose it.
              </p>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">How do I choose a target audience?</h3>
              <p className="text-gray-700">
                Common approaches for defining target audience:
                <br/><br/>
                - Demographics: Age, gender, income, education
                <br/>
                - Psychographics: Interests, values, lifestyle
                <br/>
                - Behavior: Purchase patterns, brand loyalty
                <br/>
                - Location: Geographic region, urban/rural
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Generate standardized brand names</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Support for brand value proposition</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Audience-specific naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Category-based naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>USP integration</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Consistent brand identity</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Clear market positioning</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Targeted marketing strategies</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Brand differentiation</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Customer engagement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Brand Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Brand naming is a strategic process that helps businesses create a unique identity in the market. It involves careful consideration of various components to ensure the brand name resonates with the target audience and effectively communicates the brand's value proposition.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Brand Name:</span> Unique identifier for the business
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Category:</span> Defines the product/service type
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Target Audience:</span> Specific group of customers
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Brand Value:</span> Core proposition to customers
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">USP:</span> Competitive advantage
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Brand names are typically used in marketing materials, social media, and brand guidelines to maintain consistent brand identity.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrandNamingCalculator;